import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Tag, Plus, Pencil, Trash2, Search, Save, X, Bot, Globe, Cog } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useChain } from '../context/ChainContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { upsertAddressLabel, deleteAddressLabel } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const EVM_RE = /^0x[a-fA-F0-9]{40}$/;
const SOL_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

// Route a labelled address to the right view. Un-chained (legacy) labels keep the
// legacy Krown address page; chained labels go to the multichain wallet view.
const labelHref = (r) => {
  const c = r.chain;
  if (!c || c === 'krown') return `/address/${r.address}`;
  return `/explorer/${c}/address/${r.address}`;
};

const SOURCE_BADGE = {
  telegram_bot: { label: 'Bot', icon: Bot, cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  web: { label: 'Web', icon: Globe, cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  system: { label: 'System', icon: Cog, cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
};

function SourceBadge({ source }) {
  const cfg = SOURCE_BADGE[source] || SOURCE_BADGE.web;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${cfg.cls}`}
      data-testid={`label-source-badge-${source}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function shortAddr(a) {
  if (!a) return '—';
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

export default function LabelsPage() {
  const { chainParam } = useChain();
  const [custom, setCustom] = useState([]);
  const [system, setSystem] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {address, label, isNew}
  const [busy, setBusy] = useState(false);

  const fetchLabels = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/address-labels`);
      setCustom(data.custom || []);
      setSystem(data.system || []);
    } catch (ex) {
      toast.error('Failed to load labels');
      console.error(ex);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const handleSave = async () => {
    if (!editing) return;
    const raw = (editing.address || '').trim();
    const isEvm = EVM_RE.test(raw);
    const isSol = !isEvm && SOL_RE.test(raw);
    if (!isEvm && !isSol) {
      toast.error('Invalid address — expected EVM 0x… or Solana base58');
      return;
    }
    const addr = isEvm ? raw.toLowerCase() : raw;
    const label = (editing.label || '').trim();
    if (!label) {
      toast.error('Label cannot be empty');
      return;
    }
    // EVM labels apply across every EVM chain; Solana labels are scoped to solana;
    // otherwise scope to the currently selected chain (for correct deep-linking).
    const chain = isSol ? 'solana' : (chainParam || undefined);
    setBusy(true);
    try {
      await upsertAddressLabel({ address: addr, label, source: 'web', chain });
      toast.success(`Label saved: ${label}`);
      setEditing(null);
      await fetchLabels();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (addr) => {
    if (!window.confirm(`Delete the label for ${shortAddr(addr)}?`)) return;
    setBusy(true);
    try {
      await deleteAddressLabel(addr);
      toast.success('Label deleted');
      await fetchLabels();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const allRows = useMemo(() => {
    const merged = [...custom, ...system];
    if (!search.trim()) return merged;
    const q = search.toLowerCase().trim();
    return merged.filter(r =>
      (r.address || '').toLowerCase().includes(q) ||
      (r.label || '').toLowerCase().includes(q)
    );
  }, [custom, system, search]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl" data-testid="labels-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Tag className="w-7 h-7 text-amber-500" />
            Manage Labels
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Custom labels appear across the explorer + Telegram bot. Bot labels and web labels stay in sync.
          </p>
        </div>
        <Button
          onClick={() => setEditing({ address: '', label: '', isNew: true })}
          className="gap-1.5"
          data-testid="add-label-btn"
        >
          <Plus className="w-4 h-4" /> Add Label
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg border border-border/40 bg-card/40 p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Custom Labels</p>
          <p className="text-2xl font-bold mt-1">{custom.length}</p>
        </div>
        <div className="rounded-lg border border-border/40 bg-card/40 p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">System Defaults</p>
          <p className="text-2xl font-bold mt-1">{system.length}</p>
        </div>
        <div className="rounded-lg border border-border/40 bg-card/40 p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
          <p className="text-2xl font-bold mt-1">{custom.length + system.length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search by address or label…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10"
          data-testid="labels-search-input"
        />
      </div>

      {/* Edit row */}
      {editing && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 mb-6"
          data-testid="label-edit-row">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-center">
            <Input
              type="text"
              placeholder="0x… (EVM) or Solana base58 address"
              value={editing.address}
              onChange={e => setEditing({ ...editing, address: e.target.value })}
              disabled={!editing.isNew}
              className="font-mono text-sm"
              data-testid="label-address-input"
            />
            <Input
              type="text"
              placeholder="Label (e.g. Vesting Contract)"
              value={editing.label}
              onChange={e => setEditing({ ...editing, label: e.target.value })}
              maxLength={60}
              data-testid="label-text-input"
            />
            <Button onClick={handleSave} disabled={busy} className="gap-1" data-testid="label-save-btn">
              <Save className="w-4 h-4" /> Save
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy} className="gap-1" data-testid="label-cancel-btn">
              <X className="w-4 h-4" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border/40 overflow-hidden" data-testid="labels-table">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr className="text-left text-xs uppercase text-muted-foreground tracking-wider">
              <th className="px-4 py-3">Label</th>
              <th className="px-4 py-3">Address</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && allRows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                {search ? 'No labels match your search.' : 'No labels yet — click Add Label to create one.'}
              </td></tr>
            )}
            {!loading && allRows.map((r) => (
              <tr key={r.address} className="border-t border-border/30 hover:bg-muted/20"
                data-testid={`label-row-${r.address}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.label}</div>
                  {(r.tags?.length > 0 || r.category) && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`label-meta-${r.address}`}>
                      {r.category && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/30">
                          {r.category}
                        </span>
                      )}
                      {(r.tags || []).map((t) => (
                        <span key={t} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-slate-700/50 text-slate-300">
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Link to={labelHref(r)} className="font-mono text-xs text-primary hover:underline"
                      data-testid={`label-addr-link-${r.address}`}>
                      {shortAddr(r.address)}
                    </Link>
                    {r.chain && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 uppercase" data-testid={`label-chain-${r.address}`}>{r.chain}</Badge>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3"><SourceBadge source={r.source || 'system'} /></td>
                <td className="px-4 py-3 text-right">
                  {r.source === 'system' && !r.in_system_defaults ? (
                    <span className="text-xs text-muted-foreground">read-only</span>
                  ) : (
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost"
                        onClick={() => setEditing({ address: r.address, label: r.label, isNew: false })}
                        data-testid={`label-edit-btn-${r.address}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {r.source !== 'system' && (
                        <Button size="sm" variant="ghost"
                          onClick={() => handleDelete(r.address)}
                          disabled={busy}
                          data-testid={`label-delete-btn-${r.address}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer hint */}
      <div className="mt-6 rounded-lg border border-border/40 bg-card/30 p-4 text-sm text-muted-foreground">
        <p className="flex items-center gap-2">
          <Bot className="w-4 h-4" />
          <span>
            Labels you add here also flow to your Telegram bot — alerts and group lists will use the new label
            for that address. Labels created in the bot via <code>/label</code> also appear here.
          </span>
        </p>
      </div>
    </div>
  );
}
